import { useStore } from '@nanostores/react';
import { memo, useCallback, useEffect, useState } from 'react';

import { toast } from 'react-toastify';
import { AddCustomDomain } from './AddCustomDomain';
import { AddDomainAlias } from './AddDomainAlias';
import { AppPublishedThroughNetlifyHosting } from './AppPublishedThroughNetlifyHosting';
import { BuyNewDomain } from './BuyNewDomain';
import { ConfirmCustomDomain } from './ConfirmCustomDomain';
import { ManageCustomDomains } from './ManageCustomDomains';
import { ProjectPublishedOnSubdomain } from './ProjectPublishedOnSubdomain';
import { ResolveDnsIssue } from './ResolveDnsIssue';
import { SectionContainer } from '~/components/dialogs/settings/SectionContainer';
import { Select } from '~/components/ui/Select';
import { patchHostingProvider } from '~/lib/.client/api/projects';
import { chatHooks } from '~/lib/chat-hooks';
import { importStore } from '~/lib/projects/store';
import { applicationManager } from '~/lib/stores/applicationManager';
import { domainsStore, isValidStep } from '~/lib/stores/domains';
import { projectStore } from '~/lib/stores/project';
import { invariant } from '~/utils/invariant';

export const Domains = memo(({ step }: { step?: string }) => {
  const { deploymentInfo } = useStore(importStore);
  const isDeploying = useStore(chatHooks.isDeploying);
  const domainsStep = useStore(domainsStore.modalStep);
  const currentProjectId = useStore(projectStore.projectId);

  useEffect(() => {
    if (isValidStep(step)) {
      domainsStore.modalStep.set(step);
    }
  }, [step]);

  // we definitely have a current project ID because this component is rendered within a project
  if (!currentProjectId) {
    return null;
  }

  const handleAddCustomDomain = useCallback(() => {
    domainsStore.setAddCustomDomainStep();
  }, []);

  const handleBuyNewDomain = useCallback(() => {
    domainsStore.setBuyNewDomainStep();
  }, []);

  const handleResetDomainStep = useCallback(() => {
    domainsStore.resetStep();
    domainsStore.resetDomainSearchQuery();
  }, []);

  if (domainsStep === 'addCustomDomain') {
    return <AddCustomDomain onClose={handleResetDomainStep} />;
  }

  if (domainsStep === 'buyNewDomain') {
    return <BuyNewDomain onClose={handleResetDomainStep} />;
  }

  if (domainsStep === 'purchaseConfirmation') {
    return <ConfirmCustomDomain className="h-full items-center" />;
  }

  if (domainsStep === 'resolveDNSIssue') {
    return <ResolveDnsIssue />;
  }

  if (domainsStep === 'addDomainAlias') {
    return <AddDomainAlias onClose={handleResetDomainStep} />;
  }

  return (
    <SectionContainer title="Domains & Hosting" className="gap-6">
      {!deploymentInfo && !isDeploying && <AppNotPublished />}
      {!!deploymentInfo && deploymentInfo.kind === 'static-hosting' && !deploymentInfo.is_custom_domain && (
        <ProjectPublishedOnSubdomain
          url={deploymentInfo.site_url}
          projectId={currentProjectId}
          onOpenConnectDomain={handleAddCustomDomain}
          onOpenBuyDomain={handleBuyNewDomain}
        />
      )}
      {!!deploymentInfo && deploymentInfo.kind === 'netlify' && (
        <AppPublishedThroughNetlifyHosting url={deploymentInfo.site_url} projectId={currentProjectId} />
      )}
      {!!deploymentInfo && deploymentInfo.kind === 'static-hosting' && deploymentInfo.is_custom_domain && (
        <ManageCustomDomains />
      )}
    </SectionContainer>
  );
});

Domains.displayName = 'Domains';

type HostingProvider = 'bolt' | 'netlify';

const hostingProviders: { label: string; value: HostingProvider }[] = [
  {
    label: 'Bolt Cloud',
    value: 'bolt',
  },
  {
    label: 'Netlify',
    value: 'netlify',
  },
];

const AppNotPublished = memo(() => {
  const [selectedProvider, setSelectedProvider] = useState<HostingProvider>('bolt');
  const [hasNetlifyIntegration, setHasNetlifyIntegration] = useState(false);

  useEffect(() => {
    const integrations = projectStore.integrations.get();

    if (integrations?.hosting?.data?.hostingProvider) {
      setSelectedProvider(integrations.hosting.data.hostingProvider);
    } else {
      setSelectedProvider('bolt');
    }
  }, []);

  useEffect(() => {
    async function checkNetlifyIntegration() {
      if (!applicationManager.hasConfiguration()) {
        await applicationManager.loadApplicationsConfiguration();
      }

      setHasNetlifyIntegration(applicationManager.applications.get().NETLIFY === 'CONNECTED');
    }

    checkNetlifyIntegration();
  }, []);

  const handleChangeProvider = useCallback(
    (value: string) => {
      const previousProvider = selectedProvider;

      function setProvider(provider: HostingProvider) {
        setSelectedProvider(provider);
        projectStore.integrations.set({ hosting: { data: { hostingProvider: provider } } });
      }

      setProvider(value as HostingProvider);

      const slug = projectStore.getSlug();

      invariant(slug);

      patchHostingProvider(slug, value as HostingProvider).catch(() => {
        toast.error('Failed to set hosting provider');

        setProvider(previousProvider);
      });
    },
    [selectedProvider],
  );

  return (
    <>
      <p className="text-bolt-ds-textSecondary">
        Your project is not published yet, <strong className="text-bolt-ds-textPrimary">go back to your project</strong>{' '}
        and publish it from the top right of the screen to get a unique bolt.host URL you can share.
      </p>
      {hasNetlifyIntegration && (
        <div className="flex items-center justify-between">
          <div className="w-full flex flex-col text-sm gap-1.5">
            <p className="font-semibold">Hosting provider</p>
            <p className="text-sm max-w-[85%] text-bolt-elements-textSecondary">
              Set the provider to user when publishing this project. This setting is only available before you first
              publish.
            </p>
          </div>
          <Select
            className="text-bolt-ds-textSecondary ml-auto shrink-0"
            containerClassName="w-[170px]"
            placeholder="Select"
            options={hostingProviders}
            value={selectedProvider}
            onValueChange={handleChangeProvider}
          />
        </div>
      )}
    </>
  );
});

AppNotPublished.displayName = 'DomainsAppNotPublished';
